import React, { useEffect, useState, useMemo } from "react";
import "../../../styles/Auth/productPopup.css";

const ViewProductPopup = ({ data, onClose, onStockTransfer }) => {

    const [onlinePrice, setOnlinePrice] = useState(data?.onlineSellingPrice || "");
    const [discountType, setDiscountType] = useState(data?.onlineDiscountType || "");
    const [discountValue, setDiscountValue] = useState(data?.onlineDiscountValue || "");

    const [transferType, setTransferType] = useState("offline_to_online");
    const [transferQty, setTransferQty] = useState("");
    const [colorQty, setColorQty] = useState({});
    const [offlineStocks, setOfflineStocks] = useState([]);

    const colorAttribute = data?.attributes?.find(
        (attr) => attr.attributeId?.attributeName === "Color"
    );
    const colorValues = colorAttribute?.values || [];

    /* ---------- FINAL PRICE PREVIEW (UNCHANGED) ---------- */
    const finalOnlinePrice = useMemo(() => {
        let price = Number(onlinePrice) || 0;
        if (!price) return 0;

        if (discountType === "FlatDiscount") {
            price -= Number(discountValue || 0);
        }

        if (discountType === "PercentageDiscount") {
            price -= (price * Number(discountValue || 0)) / 100;
        }

        return price > 0 ? Math.round(price) : 0;
    }, [onlinePrice, discountType, discountValue]);

    const isPriceValid = Number(onlinePrice) > 0;

    /* ---------- INIT COLOR OBJECT ---------- */
    useEffect(() => {
        if (colorValues.length > 0) {
            const initial = {};
            colorValues.forEach((color) => {
                initial[color] = {};
            });
            setColorQty(initial);
        }
    }, [data]);

    useEffect(() => {
        setOnlinePrice(data?.onlineSellingPrice || "");
        setDiscountType(data?.onlineDiscountType || "");
        setDiscountValue(data?.onlineDiscountValue || "");
    }, [data]);

    /* ---------- FETCH OFFLINE STOCK ---------- */
    useEffect(() => {
        fetchOfflineStocks();
    }, []);

    const fetchOfflineStocks = async () => {
        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/products/stock/offline/${data._id}`
        );
        const json = await res.json();
        setOfflineStocks(json.data || []);
    };

    /* ---------- TRANSFER (UNCHANGED) ---------- */
    const handleTransfer = async () => {
        if (!isPriceValid) return;

        const transfers = [];

        Object.entries(colorQty).forEach(([color, val]) => {
            if (val?.quantity > 0 && val?.fromStockId) {
                transfers.push({
                    fromStockId: val.fromStockId,
                    color,
                    quantity: val.quantity
                });
            }
        });

        if (transfers.length === 0) {
            alert("Select batch and quantity");
            return;
        }

        await fetch(
            `${process.env.REACT_APP_API_URL}/products/stock/transfer/${data._id}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    productId: data._id,
                    transfers
                })
            }
        );

        alert("Stock transferred successfully");
        onClose();
    };

    const handleSaveOnlinePrice = async () => {
        if (!onlinePrice || Number(onlinePrice) <= 0) {
            alert("Enter valid online price");
            return;
        }

        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/products/update-online-price`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    productId: data._id,
                    onlineSellingPrice: Number(onlinePrice),
                    onlineDiscountType: discountType || null,
                    onlineDiscountValue: Number(discountValue || 0)
                })
            }
        );

        const json = await res.json();

        if (json.success) {
            alert("Online price saved successfully");
        } else {
            alert(json.message || "Failed to update price");
        }
    };


    return (
        <div className="view-popup-overlay">
            <div className="view-popup-wrapper">
                <div className="view-product-popup-card">

                    <h6 className="view-product-popup-title">Product Details</h6>

                    {/* SCROLLABLE CONTENT */}
                    <div className="product-view-popup-info">

                        {/* IMAGE (TOP) */}
                        <div className="product-view-image">
                            <img
                                src={`${process.env.REACT_APP_API_URL}${data.productImageObject?.uri}`}
                                alt="product"
                                className="product-view-popup-image"
                            />
                        </div>

                        {/* TITLE */}
                        <div className="Product-popup-title">
                            <h6>{data.title}</h6>
                        </div>

                        {/* BASIC DETAILS */}
                        <div className="product-stock-sold-container">
                            <div className="product-stock-sold">

                                <div className="product-sold">
                                    <div className="product-count">{data.offlineStock || 0}</div>
                                    <div className="product-label">Offline</div>
                                </div>

                                <div className="product-vertical-divider"></div>

                                <div className="product-stock">
                                    <div className="product-count">{data.onlineStock || 0}</div>
                                    <div className="product-label">Online</div>
                                </div>

                            </div>
                        </div>

                        {/* PRICE SECTION */}
                        <div className="product-price-section">

                            <div className="product-price-details">
                                <div className="product-price-row">
                                    <span>Online Price</span>
                                    <span>₹{onlinePrice || 0}</span>
                                </div>
                            </div>

                            <div className="product-price-detail">
                                <div className="product-price-row1">
                                    <span>Final Price</span>
                                    <span>₹{finalOnlinePrice}</span>
                                </div>
                            </div>

                        </div>

                        <div className="product-price-settings">
                            <h6 className="product-section-title">Online Price Settings</h6>

                            <div className="price-setting-grid">

                                <div className="price-field">
                                    <label>Online Selling Price</label>
                                    <input
                                        type="number"
                                        value={onlinePrice}
                                        onChange={(e) => setOnlinePrice(e.target.value)}
                                        placeholder="Enter price"
                                    />
                                </div>

                                <div className="price-field">
                                    <label>Discount Type</label>
                                    <select
                                        value={discountType}
                                        onChange={(e) => setDiscountType(e.target.value)}
                                    >
                                        <option value="">No Discount</option>
                                        <option value="FlatDiscount">Flat Discount</option>
                                        <option value="PercentageDiscount">Percentage Discount</option>
                                    </select>
                                </div>

                                <div className="price-field">
                                    <label>Discount Value</label>
                                    <input
                                        type="number"
                                        value={discountValue}
                                        disabled={!discountType}
                                        onChange={(e) => setDiscountValue(e.target.value)}
                                        placeholder="0"
                                    />
                                </div>

                            </div>

                            <div className="final-price-preview">
                                Final Online Price :
                                <strong> ₹{finalOnlinePrice}</strong>
                            </div>

                            {!isPriceValid && (
                                <p className="warning-text">
                                    Please set online selling price to enable stock transfer
                                </p>
                            )}
                            <button
                                className="save-online-price-btn"
                                onClick={handleSaveOnlinePrice}
                            >
                                Save Online Price
                            </button>
                        </div>

                        {/* STOCK TRANSFER */}
                        <div className="product-transfer-section">
                            <h6 className="product-section-title">Stock Transfer</h6>

                            {!isPriceValid && (
                                <p className="warning-text">
                                    Set online price before transferring stock
                                </p>
                            )}

                            <div className="color-transfer-box">
                                <h6 className="attr-title">Color-wise Quantity</h6>

                                {colorValues.map(color => (
                                    <div className="color-row" key={color}>
                                        <label>{color}</label>

                                        <select
                                            className="color-input"
                                            disabled={!isPriceValid}
                                            onChange={(e) =>
                                                setColorQty({
                                                    ...colorQty,
                                                    [color]: {
                                                        ...colorQty[color],
                                                        fromStockId: e.target.value
                                                    }
                                                })
                                            }
                                        >
                                            <option value="">Select Batch</option>
                                            {offlineStocks.map(stock => (
                                                <option key={stock._id} value={stock._id}>
                                                    Qty {stock.offlineQuantity}
                                                </option>
                                            ))}
                                        </select>

                                        <input
                                            type="number"
                                            className="color-input"
                                            placeholder="Qty"
                                            disabled={!isPriceValid}
                                            onChange={(e) =>
                                                setColorQty({
                                                    ...colorQty,
                                                    [color]: {
                                                        ...colorQty[color],
                                                        quantity: Number(e.target.value)
                                                    }
                                                })
                                            }
                                        />
                                    </div>
                                ))}
                            </div>

                            <button
                                className="product-transfer-button"
                                disabled={!isPriceValid}
                                onClick={handleTransfer}
                            >
                                Transfer
                            </button>
                        </div>

                        <button className="product-Done-Button" onClick={onClose}>
                            Close
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProductPopup;
